import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { getRecordingForSubmission } from '../services/recordingsService';
import {
  Filter, ChevronDown, CheckCircle, Clock, User,
  BookOpen, ArrowRight, Send, AlertCircle, Mic
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function FeedbackFormA1A2({ onSubmit, loading }) {
  const [corrections, setCorrections] = useState('');
  const [explanation, setExplanation] = useState('');
  const [comment, setComment] = useState('');
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-bold text-[#1A2F4A] uppercase tracking-wider">Basic Correction (A1–A2)</h4>
      <div>
        <label className="text-xs text-[#6B4E37] block mb-1">Corrected sentences</label>
        <textarea value={corrections} onChange={e => setCorrections(e.target.value)} rows={4} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none" />
      </div>
      <div>
        <label className="text-xs text-[#6B4E37] block mb-1">Simple explanation</label>
        <textarea value={explanation} onChange={e => setExplanation(e.target.value)} rows={3} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none" />
      </div>
      <div>
        <label className="text-xs text-[#6B4E37] block mb-1">Comment (optional)</label>
        <textarea value={comment} onChange={e => setComment(e.target.value)} rows={2} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none" />
      </div>
      <button onClick={() => onSubmit({ a1a2: { corrections, explanation, comment } })} disabled={loading || !corrections.trim()} className="w-full bg-[#1A2F4A] text-white py-2.5 rounded-xl text-sm font-bold hover:bg-[#243a58] transition-colors disabled:opacity-40" data-testid="submit-review-btn">
        {loading ? 'Submitting...' : 'Submit Review'}
      </button>
    </div>
  );
}

function FeedbackFormB1({ onSubmit, loading }) {
  const [feedback, setFeedback] = useState('');
  const [corrections, setCorrections] = useState('');
  const [advice, setAdvice] = useState('');
  const [score, setScore] = useState('');
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-bold text-[#1A2F4A] uppercase tracking-wider">Intermediate Correction (B1)</h4>
      <div>
        <label className="text-xs text-[#6B4E37] block mb-1">Short feedback</label>
        <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={3} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none" />
      </div>
      <div>
        <label className="text-xs text-[#6B4E37] block mb-1">Key corrections</label>
        <textarea value={corrections} onChange={e => setCorrections(e.target.value)} rows={4} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none" />
      </div>
      <div>
        <label className="text-xs text-[#6B4E37] block mb-1">Brief advice</label>
        <textarea value={advice} onChange={e => setAdvice(e.target.value)} rows={2} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none" />
      </div>
      <div>
        <label className="text-xs text-[#6B4E37] block mb-1">Score (optional, /20)</label>
        <input type="number" min={0} max={20} value={score} onChange={e => setScore(e.target.value)} className="w-20 text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 focus:border-[#D4AF37] focus:outline-none" />
      </div>
      <button onClick={() => onSubmit({ b1: { feedback, corrections, advice, score: score ? parseInt(score) : null } })} disabled={loading || !feedback.trim()} className="w-full bg-[#1A2F4A] text-white py-2.5 rounded-xl text-sm font-bold hover:bg-[#243a58] transition-colors disabled:opacity-40" data-testid="submit-review-btn">
        {loading ? 'Submitting...' : 'Submit Review'}
      </button>
    </div>
  );
}

function FeedbackFormB2({ onSubmit, loading }) {
  const [scores, setScores] = useState({ comp: '', ref: '', arg: '', lang: '' });
  const [pointsForts, setPointsForts] = useState('');
  const [pointsAmeliorer, setPointsAmeliorer] = useState('');
  const [corrections, setCorrections] = useState('');
  const [conseils, setConseils] = useState('');

  const total = (parseInt(scores.comp) || 0) + (parseInt(scores.ref) || 0) + (parseInt(scores.arg) || 0) + (parseInt(scores.lang) || 0);

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-bold text-[#1A2F4A] uppercase tracking-wider">Full DELF B2 Evaluation</h4>
      <div className="grid grid-cols-2 gap-2">
        {[
          { key: 'comp', label: 'Compréhension (/5)' },
          { key: 'ref', label: 'Reformulation (/5)' },
          { key: 'arg', label: 'Argumentation (/5)' },
          { key: 'lang', label: 'Langue (/5)' },
        ].map(({ key, label }) => (
          <div key={key}>
            <label className="text-xs text-[#6B4E37] block mb-1">{label}</label>
            <input type="number" min={0} max={5} value={scores[key]} onChange={e => setScores(prev => ({ ...prev, [key]: e.target.value }))} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 focus:border-[#D4AF37] focus:outline-none" />
          </div>
        ))}
      </div>
      <div className="bg-[#FAF7F0] rounded-lg p-3 text-center">
        <span className="text-lg font-bold text-[#1A2F4A]" style={{ fontFamily: "'Playfair Display', serif" }}>{total}/20</span>
      </div>
      <div>
        <label className="text-xs text-[#6B4E37] block mb-1">Points forts</label>
        <textarea value={pointsForts} onChange={e => setPointsForts(e.target.value)} rows={3} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none" />
      </div>
      <div>
        <label className="text-xs text-[#6B4E37] block mb-1">Points à améliorer</label>
        <textarea value={pointsAmeliorer} onChange={e => setPointsAmeliorer(e.target.value)} rows={3} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none" />
      </div>
      <div>
        <label className="text-xs text-[#6B4E37] block mb-1">Corrections ciblées</label>
        <textarea value={corrections} onChange={e => setCorrections(e.target.value)} rows={4} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none" />
      </div>
      <div>
        <label className="text-xs text-[#6B4E37] block mb-1">Conseils méthodologiques</label>
        <textarea value={conseils} onChange={e => setConseils(e.target.value)} rows={3} className="w-full text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 focus:border-[#D4AF37] focus:outline-none resize-none" />
      </div>
      <button onClick={() => onSubmit({ b2: { score_total: total, score_comprehension: parseInt(scores.comp) || 0, score_reformulation: parseInt(scores.ref) || 0, score_argumentation: parseInt(scores.arg) || 0, score_langue: parseInt(scores.lang) || 0, points_forts: pointsForts, points_ameliorer: pointsAmeliorer, corrections, conseils } })} disabled={loading} className="w-full bg-[#1A2F4A] text-white py-2.5 rounded-xl text-sm font-bold hover:bg-[#243a58] transition-colors disabled:opacity-40" data-testid="submit-review-btn">
        {loading ? 'Submitting...' : 'Submit Full Evaluation'}
      </button>
    </div>
  );
}

function OralRecordingBlock({ submissionId }) {
  const [state, setState] = useState({ loading: true, url: '', meta: null, error: '' });

  useEffect(() => {
    let alive = true;
    getRecordingForSubmission(submissionId)
      .then((data) => {
        if (!alive) return;
        setState({ loading: false, url: data.playback_url, meta: data.recording, error: '' });
      })
      .catch((err) => {
        if (!alive) return;
        setState({ loading: false, url: '', meta: null, error: err.message || 'Unable to load recording' });
      });
    return () => { alive = false; };
  }, [submissionId]);

  if (state.loading) {
    return <p className="text-xs text-[#999] italic" data-testid="oral-loading">Loading recording…</p>;
  }
  if (state.error) {
    return <p className="text-xs text-red-600" data-testid="oral-error">{state.error}</p>;
  }
  return (
    <div className="bg-[#1A2F4A] rounded-xl p-4" data-testid={`oral-block-${submissionId}`}>
      <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <Mic className="w-3 h-3" /> Student oral recording
      </p>
      <audio
        controls
        src={state.url}
        className="w-full"
        data-testid="oral-audio"
      />
      {state.meta?.duration_seconds != null && (
        <p className="text-[10px] text-white/60 mt-1.5">
          Duration ≈ {Math.round(state.meta.duration_seconds)}s · {(state.meta.size_bytes / 1024).toFixed(0)} KB
        </p>
      )}
      {state.meta?.prompt_text && (
        <p className="text-[11px] text-white/70 mt-2 italic">Prompt: "{state.meta.prompt_text}"</p>
      )}
    </div>
  );
}

function SubmissionCard({ sub, onReview }) {
  const [expanded, setExpanded] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmitReview = async (feedbackData) => {
    setLoading(true);
    await onReview(sub.id, sub.level, feedbackData);
    setLoading(false);
    setReviewing(false);
  };

  const levelColor = { A1: 'bg-green-100 text-green-800', A2: 'bg-blue-100 text-blue-800', B1: 'bg-amber-100 text-amber-800', B2: 'bg-purple-100 text-purple-800' };

  return (
    <div className="bg-white border border-[#E8E4DC] rounded-xl overflow-hidden" data-testid={`teacher-sub-${sub.id}`}>
      <div className="px-5 py-4 flex items-center gap-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${sub.status === 'pending' ? 'bg-amber-400' : 'bg-emerald-500'}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${levelColor[sub.level] || 'bg-gray-100 text-gray-800'}`}>{sub.level}</span>
            {(sub.submission_type === 'oral' || sub.recording_id) && (
              <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#1A2F4A] text-[#D4AF37] flex items-center gap-1" data-testid="oral-badge">
                <Mic className="w-2.5 h-2.5" /> Oral
              </span>
            )}
            <span className="text-sm font-semibold text-[#1A2F4A] truncate">{sub.exercise_title}</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-[#999]">
            <span className="flex items-center gap-1"><User className="w-3 h-3" />{sub.student_name || sub.student_email}</span>
            <span>{new Date(sub.created_at).toLocaleDateString()}</span>
            {sub.attempts?.length > 0 && <span className="text-[#D4AF37]">Attempt {sub.attempts.length + 1}</span>}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-[#999] transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </div>

      {expanded && (
        <div className="px-5 pb-5 border-t border-[#E8E4DC] pt-4 space-y-4">
          {/* Oral recording (if this is an oral submission) */}
          {(sub.submission_type === 'oral' || sub.recording_id) && (
            <OralRecordingBlock submissionId={sub.id} />
          )}

          {/* Student answers */}
          {sub.answers?.taskA?.some(a => a) && (
            <div>
              <p className="text-xs font-bold text-[#1A2F4A] uppercase tracking-wider mb-2">Task A — Student Answers</p>
              <div className="space-y-1.5">
                {sub.answers.taskA.map((a, i) => a && (
                  <div key={i} className="bg-[#FAF7F0] rounded-lg p-3">
                    <p className="text-xs text-[#999] mb-0.5">Q{i + 1}</p>
                    <p className="text-sm text-[#1A2F4A]">{a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {sub.answers?.taskB?.some(a => a) && (
            <div>
              <p className="text-xs font-bold text-[#1A2F4A] uppercase tracking-wider mb-2">Task B — Reformulation</p>
              <div className="space-y-1.5">
                {sub.answers.taskB.map((a, i) => a && (
                  <div key={i} className="bg-[#FAF7F0] rounded-lg p-3">
                    <p className="text-sm text-[#1A2F4A]">{a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {sub.answers?.taskC && (
            <div>
              <p className="text-xs font-bold text-[#1A2F4A] uppercase tracking-wider mb-2">Task C — Argumentation</p>
              <div className="bg-[#FAF7F0] rounded-lg p-3">
                <p className="text-sm text-[#1A2F4A]">{sub.answers.taskC}</p>
              </div>
            </div>
          )}

          {/* Existing feedback (if reviewed) */}
          {sub.status === 'reviewed' && sub.feedback && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-xs font-bold text-emerald-800 uppercase tracking-wider mb-2">Feedback Provided</p>
              {sub.feedback.corrections && <p className="text-sm text-emerald-900 mb-1"><strong>Corrections:</strong> {sub.feedback.corrections}</p>}
              {sub.feedback.explanation && <p className="text-sm text-emerald-900 mb-1"><strong>Explanation:</strong> {sub.feedback.explanation}</p>}
              {sub.feedback.feedback && <p className="text-sm text-emerald-900 mb-1"><strong>Feedback:</strong> {sub.feedback.feedback}</p>}
              {sub.feedback.advice && <p className="text-sm text-emerald-900 mb-1"><strong>Advice:</strong> {sub.feedback.advice}</p>}
              {sub.feedback.points_forts && <p className="text-sm text-emerald-900 mb-1"><strong>Points forts:</strong> {sub.feedback.points_forts}</p>}
              {sub.feedback.points_ameliorer && <p className="text-sm text-emerald-900 mb-1"><strong>Points à améliorer:</strong> {sub.feedback.points_ameliorer}</p>}
              {sub.feedback.conseils && <p className="text-sm text-emerald-900 mb-1"><strong>Conseils:</strong> {sub.feedback.conseils}</p>}
              {sub.feedback.score_total != null && <p className="text-sm font-bold text-emerald-900">Score: {sub.feedback.score_total}/20</p>}
              {sub.feedback.score != null && <p className="text-sm font-bold text-emerald-900">Score: {sub.feedback.score}/20</p>}
              <p className="text-xs text-emerald-600 mt-2">Reviewed by {sub.reviewed_by} on {new Date(sub.reviewed_at).toLocaleDateString()}</p>
            </div>
          )}

          {/* Review form */}
          {sub.status === 'pending' && !reviewing && (
            <button onClick={() => setReviewing(true)} className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-[#1A2F4A] py-2.5 rounded-xl text-sm font-bold hover:bg-[#C9A961] transition-colors" data-testid={`start-review-${sub.id}`}>
              <BookOpen className="w-4 h-4" /> Write Review
            </button>
          )}
          {reviewing && (
            <div className="border-t border-[#E8E4DC] pt-4">
              {(sub.level === 'A1' || sub.level === 'A2') && <FeedbackFormA1A2 onSubmit={handleSubmitReview} loading={loading} />}
              {sub.level === 'B1' && <FeedbackFormB1 onSubmit={handleSubmitReview} loading={loading} />}
              {sub.level === 'B2' && <FeedbackFormB2 onSubmit={handleSubmitReview} loading={loading} />}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TeacherDashboard() {
  const { user, token } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [filterLevel, setFilterLevel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [loadingData, setLoadingData] = useState(true);

  const fetchSubmissions = useCallback(async () => {
    if (!token) return;
    const params = new URLSearchParams();
    if (filterLevel) params.set('level', filterLevel);
    if (filterStatus) params.set('status', filterStatus);
    try {
      const res = await fetch(`${API}/api/submissions/teacher?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setSubmissions(await res.json());
    } catch (err) {
      // Network flake — leave the last-known submissions list in place instead of clearing.
      console.warn('TeacherDashboard: fetchSubmissions failed', err);
    }
    setLoadingData(false);
  }, [token, filterLevel, filterStatus]);

  useEffect(() => { fetchSubmissions(); }, [fetchSubmissions]);

  const handleReview = async (subId, level, feedbackData) => {
    await fetch(`${API}/api/submissions/${subId}/review`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ level, ...feedbackData }),
    });
    fetchSubmissions();
  };

  if (!user || (user.role !== 'teacher' && user.role !== 'admin')) {
    return (
      <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
          <p className="text-lg font-bold text-[#1A2F4A] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Teacher Access Required</p>
          <p className="text-sm text-[#999]">This page is restricted to teacher accounts.</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-4 text-sm text-[#D4AF37] font-semibold hover:underline">
            <ArrowRight className="w-4 h-4 rotate-180" /> Return Home
          </Link>
        </div>
      </div>
    );
  }

  const pending = submissions.filter(s => s.status === 'pending').length;
  const reviewed = submissions.filter(s => s.status === 'reviewed').length;
  const oralCount = submissions.filter(s => s.submission_type === 'oral' || s.recording_id).length;

  const visibleSubmissions = filterType === 'oral'
    ? submissions.filter(s => s.submission_type === 'oral' || s.recording_id)
    : filterType === 'written'
      ? submissions.filter(s => !(s.submission_type === 'oral' || s.recording_id))
      : submissions;

  return (
    <div className="min-h-screen bg-[#FAF7F0]">
      <section className="pt-20 pb-8">
        <div className="max-w-[800px] mx-auto px-8">
          <p className="text-xs text-[#D4AF37] font-bold uppercase tracking-wider mb-2">Teacher Dashboard</p>
          <h1 className="text-3xl font-bold text-[#1A2F4A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="teacher-dashboard-title">
            Student Submissions
          </h1>
          <div className="flex items-center gap-4 mt-4 text-sm flex-wrap">
            <span className="flex items-center gap-1.5 text-amber-600" data-testid="stat-pending"><Clock className="w-4 h-4" /> {pending} pending</span>
            <span className="flex items-center gap-1.5 text-emerald-600" data-testid="stat-reviewed"><CheckCircle className="w-4 h-4" /> {reviewed} reviewed</span>
            <span className="flex items-center gap-1.5 text-[#1A2F4A]" data-testid="stat-oral"><Mic className="w-4 h-4 text-[#D4AF37]" /> {oralCount} oral recordings</span>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-6">
        <div className="max-w-[800px] mx-auto px-8 flex items-center gap-3 flex-wrap">
          <Filter className="w-4 h-4 text-[#999]" />
          <select value={filterLevel} onChange={e => setFilterLevel(e.target.value)} className="text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white focus:border-[#D4AF37] focus:outline-none" data-testid="filter-level">
            <option value="">All Levels</option>
            {['A1', 'A2', 'B1', 'B2'].map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white focus:border-[#D4AF37] focus:outline-none" data-testid="filter-status">
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="text-sm border border-[#E8E4DC] rounded-lg px-3 py-2 bg-white focus:border-[#D4AF37] focus:outline-none" data-testid="filter-type">
            <option value="">All Types</option>
            <option value="oral">Speaking Recordings</option>
            <option value="written">Written Only</option>
          </select>
        </div>
      </section>

      {/* Submissions */}
      <section className="pb-20">
        <div className="max-w-[800px] mx-auto px-8">
          {loadingData ? (
            <p className="text-center text-sm text-[#999] py-10">Loading submissions...</p>
          ) : visibleSubmissions.length === 0 ? (
            <div className="text-center py-16">
              <Send className="w-8 h-8 text-[#E8E4DC] mx-auto mb-3" />
              <p className="text-sm text-[#999]" data-testid="no-submissions">No submissions match these filters</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="submissions-list">
              {visibleSubmissions.map(sub => (
                <SubmissionCard key={sub.id} sub={sub} onReview={handleReview} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
